import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Check, Mail, ArrowUpRight, Music } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { track } from "@/lib/analytics";
import { SKOOL_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[11px] tracking-[0.14em] uppercase text-white/45">{label}</Label>
    {children}
  </div>
);

export const RoadmapGenerator = () => {
  const [step, setStep] = useState("intro"); // intro | lead | song | result
  const [loading, setLoading] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [lead, setLead] = useState({ name: "", phone: "", email: "" });
  const [song, setSong] = useState({ song_name: "", lyrics: "", release_date: "", description: "" });
  const [result, setResult] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [emailed, setEmailed] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.phone || !lead.email.includes("@")) {
      toast.error("Please add your name, phone and a valid email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/roadmap/lead`, lead);
      setLeadId(data.lead_id);
      track("roadmap_lead", "unlocked");
      setStep("song");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!song.song_name || !song.lyrics || !song.description) {
      toast.error("Add the song name, lyrics and what it's about.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/roadmap/generate`, { lead_id: leadId, ...song });
      setResult(data.roadmap);
      setSubmissionId(data.submission_id);
      setStep("result");
      setTimeout(() => document.querySelector("#roadmap")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate right now. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const emailMe = async () => {
    try {
      await axios.post(`${API}/roadmap/${submissionId}/email`);
      setEmailed(true);
      toast.success("Roadmap sent to your inbox.");
    } catch {
      toast.error("Couldn't email it. Try again.");
    }
  };

  return (
    <section id="roadmap" className="relative py-28 lg:py-36 bg-[#050505]" data-testid="roadmap-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div
          className="relative rounded-3xl border border-white/12 overflow-hidden p-8 lg:p-14"
          style={{ background: "radial-gradient(120% 120% at 50% 0%, rgba(232,180,90,0.10) 0%, rgba(255,255,255,0) 55%), #0A0A0A" }}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 mb-7">
              <Sparkles size={14} className="text-gold" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-white/60">Free Tool</span>
            </div>
            <h2 className="font-heading uppercase tracking-tight text-white text-4xl lg:text-6xl leading-[0.95]">
              Release Roadmap <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">generator</span>
            </h2>
            <p className="text-white/55 mt-5 leading-relaxed text-[15px]">
              Drop in your next single and get a personalised rollout plan built around your actual lyrics and story — not a generic checklist. Made by someone who does this for a living.
            </p>
          </div>

          <div className="mt-10 max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              {step === "intro" && (
                <motion.div key="intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="text-center">
                  <button
                    onClick={() => { setStep("lead"); track("roadmap_start", "intro"); }}
                    data-testid="roadmap-start"
                    className="group inline-flex items-center gap-2 px-9 py-4 rounded-full bg-gold text-black text-[13px] tracking-[0.14em] uppercase font-medium hover:-translate-y-1 transition-transform duration-300"
                  >
                    Generate My Roadmap <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              )}

              {step === "lead" && (
                <motion.form key="lead" onSubmit={submitLead} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="rounded-2xl border border-white/10 bg-black/40 p-7 lg:p-8 space-y-5" data-testid="roadmap-lead-form">
                  <p className="text-[12px] tracking-[0.16em] uppercase text-white/45 text-center">Step 1 · Where should we send it</p>
                  <Field label="Full name *"><Input data-testid="rm-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="dark-input" /></Field>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Phone number *"><Input data-testid="rm-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="+44 …" className="dark-input" /></Field>
                    <Field label="Email address *"><Input data-testid="rm-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" className="dark-input" /></Field>
                  </div>
                  <button type="submit" disabled={loading} data-testid="rm-lead-submit" className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold text-black text-[13px] tracking-[0.14em] uppercase font-medium hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <>Unlock the Generator <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>}
                  </button>
                  <p className="text-white/30 text-[12px] text-center">Your details stay private. Used only to send your roadmap and follow up.</p>
                </motion.form>
              )}

              {step === "song" && (
                <motion.form key="song" onSubmit={generate} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="rounded-2xl border border-white/10 bg-black/40 p-7 lg:p-8 space-y-5" data-testid="roadmap-song-form">
                  <p className="text-[12px] tracking-[0.16em] uppercase text-gold text-center">Step 2 · Tell me about the song</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Song name *"><Input data-testid="rm-song" value={song.song_name} onChange={(e) => setSong({ ...song, song_name: e.target.value })} placeholder="Track title" className="dark-input" /></Field>
                    <Field label="Estimated release date"><Input data-testid="rm-date" type="date" value={song.release_date} onChange={(e) => setSong({ ...song, release_date: e.target.value })} className="dark-input [color-scheme:dark]" /></Field>
                  </div>
                  <Field label="Lyrics (full or partial) *">
                    <Textarea data-testid="rm-lyrics" value={song.lyrics} onChange={(e) => setSong({ ...song, lyrics: e.target.value })} rows={5} placeholder="Paste your lyrics here…" className="bg-white/[0.04] border-white/15 text-white rounded-lg resize-none placeholder:text-white/30" />
                  </Field>
                  <Field label="What's the song about? *">
                    <Textarea data-testid="rm-desc" value={song.description} onChange={(e) => setSong({ ...song, description: e.target.value })} rows={3} placeholder="e.g. a song about moving on from a relationship that held you back — themes of independence and self-respect." className="bg-white/[0.04] border-white/15 text-white rounded-lg resize-none placeholder:text-white/30" />
                  </Field>
                  <button type="submit" disabled={loading} data-testid="rm-generate" className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold text-black text-[13px] tracking-[0.14em] uppercase font-medium hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Building your roadmap…</> : <>Generate Roadmap <Sparkles size={16} /></>}
                  </button>
                </motion.form>
              )}

              {step === "result" && result && (
                <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-black/40 p-7 lg:p-9" data-testid="roadmap-result">
                  <div className="flex items-center gap-2 text-gold mb-4"><Music size={16} /><span className="text-[12px] tracking-[0.16em] uppercase">{song.song_name} — your roadmap</span></div>
                  {result.theme_summary && <p className="font-serif-accent italic text-lg lg:text-xl text-white/80 leading-snug mb-8">{result.theme_summary}</p>}

                  {(result.pre_release || []).map((b, i) => (
                    <div key={i} className="mb-6" data-testid={`rm-pre-${i}`}>
                      <h4 className="font-heading uppercase tracking-tight text-white text-lg mb-2">{b.window}</h4>
                      <ul className="space-y-1.5">{(b.ideas || []).map((x, j) => <li key={j} className="flex gap-3 text-white/60 text-[14px]"><span className="text-gold">›</span>{x}</li>)}</ul>
                    </div>
                  ))}

                  {result.release_day?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-heading uppercase tracking-tight text-white text-lg mb-2">Release Day</h4>
                      <ul className="space-y-1.5">{result.release_day.map((x, j) => <li key={j} className="flex gap-3 text-white/60 text-[14px]"><Check size={15} className="text-gold mt-0.5 shrink-0" />{x}</li>)}</ul>
                    </div>
                  )}
                  {result.post_release?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-heading uppercase tracking-tight text-white text-lg mb-2">Post-Release Push</h4>
                      <ul className="space-y-1.5">{result.post_release.map((x, j) => <li key={j} className="flex gap-3 text-white/60 text-[14px]"><span className="text-gold">›</span>{x}</li>)}</ul>
                    </div>
                  )}
                  {result.hooks?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="font-heading uppercase tracking-tight text-white text-lg mb-2">Content Hooks</h4>
                      <div className="grid gap-2">{result.hooks.map((x, j) => <div key={j} className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white/75 text-[14px]" data-testid={`rm-hook-${j}`}>"{x}"</div>)}</div>
                    </div>
                  )}

                  <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row gap-3">
                    <button onClick={emailMe} disabled={emailed} data-testid="rm-email-me" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gold text-black text-[12px] tracking-[0.12em] uppercase font-medium hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
                      {emailed ? <><Check size={15} /> Sent to your inbox</> : <><Mail size={15} /> Email it to me</>}
                    </button>
                    <a href="#book-call" onClick={() => track("booking_click", "roadmap")} data-testid="rm-book" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white text-[12px] tracking-[0.12em] uppercase hover:bg-white/10 hover:text-gold transition-colors duration-300">
                      Book a Call
                    </a>
                    <a href={SKOOL_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("skool_click", "roadmap")} data-testid="rm-skool" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white text-[12px] tracking-[0.12em] uppercase hover:bg-white/10 hover:text-gold transition-colors duration-300">
                      Join Skool <ArrowUpRight size={14} />
                    </a>
                  </div>
                  <p className="text-white/40 text-[13px] mt-5 leading-relaxed">Want help actually executing this rollout? That's exactly what the sessions and creative partnerships are for.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapGenerator;
